// src/hooks/usePreguntasRespuestasFinal.js
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const DF_URL = `${API_BASE_URL}/api/resultados/desafio-final`;

// ---- util: barajar (Fisher–Yates, sin mutar) ----
function shuffleArray(arr = []) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const usePreguntasRespuestasFinal = (nivelIdParam) => {
  const nivelId = Number(nivelIdParam);
  const navigate = useNavigate();

  const [preguntas, setPreguntas] = useState([]);
  const [preguntaActual, setPreguntaActual] = useState(null);
  const [preguntaIndex, setPreguntaIndex] = useState(0);

  const [respuestaSeleccionada, setRespuestaSeleccionada] = useState(null);
  const [nivelCompletado, setNivelCompletado] = useState(false);

  const [tiempoRestante, setTiempoRestante] = useState(null);
  const [tiempoAgotadoFlag, setTiempoAgotadoFlag] = useState(false);

  const [mostrarAlerta, setMostrarAlerta] = useState(false);
  const [alertaInfo, setAlertaInfo] = useState({
    esCorrecta: false,
    mensaje: "",
  });
  const [retroalimentacion, setRetroalimentacion] = useState("");
  const [mensaje, setMensaje] = useState(null);
  const [mostrarResultado, setMostrarResultado] = useState(false);

  // Resumen respuestas y fallos
  const [respuestasMap, setRespuestasMap] = useState({});
  const [fallos, setFallos] = useState(0);

  // Control de doble guardado en StrictMode
  const finalSaveOnce = useRef(false);

  // Interval del temporizador
  const timerRef = useRef(null);
  const pauseTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  function formatTiempo(segundos) {
    const minutos = Math.floor(segundos / 60);
    const segundosRestantes = segundos % 60;
    return `${minutos.toString().padStart(2, "0")}:${segundosRestantes
      .toString()
      .padStart(2, "0")}`;
  }

  useEffect(() => {
    cargarPreguntas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nivelId]);

  // Temporizador: corre solo si no hay alerta, no terminó ni se agotó el tiempo
  useEffect(() => {
    const debeCorrer =
      tiempoRestante !== null &&
      tiempoRestante > 0 &&
      !mostrarAlerta &&
      !tiempoAgotadoFlag &&
      !nivelCompletado;

    if (debeCorrer && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setTiempoRestante((prev) => {
          if (prev <= 1) {
            pauseTimer();
            setTiempoAgotadoFlag(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Si no debe correr (por alerta/fin/agotado), asegúrate de pausar
      pauseTimer();
    }

    return () => {
      pauseTimer();
    };
  }, [tiempoRestante, mostrarAlerta, tiempoAgotadoFlag, nivelCompletado]);

  useEffect(() => {
    if (tiempoAgotadoFlag) {
      tiempoAgotado();
    }
  }, [tiempoAgotadoFlag]);

  const cargarPreguntas = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(
        `${API_BASE_URL}/api/preguntas/desafio-final/${nivelId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPreguntas(data);

      // Pregunta inicial con RESPUESTAS barajadas
      const first = data[0];
      const respuestasValidas = (first?.respuestas || []).filter(
        (r) => (r.texto_respuesta || "").trim() !== ""
      );
      setPreguntaActual(
        first ? { ...first, respuestas: shuffleArray(respuestasValidas) } : null
      );

      setPreguntaIndex(0);
      setNivelCompletado(false);
      setTiempoRestante(first?.limite_tiempo ?? 60);
      setRespuestasMap({});
      setFallos(0);
      setTiempoAgotadoFlag(false);
    } catch (error) {
      console.error("Error al cargar preguntas desafío final:", error);
    }
  };

  const handleRespuesta = async (respuestaId) => {
    // Pausa de inmediato para que el conteo no avance durante verificación y overlay
    pauseTimer();

    setRespuestaSeleccionada(respuestaId);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${API_BASE_URL}/api/preguntas/verificar`,
        { pregunta_id: preguntaActual.id, respuesta_id: respuestaId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const esCorrecta = !!data.es_correcta;
      setRetroalimentacion(data.retroalimentacion);

      // Marca la pregunta como respondida correctamente (no retrocede)
      setRespuestasMap((prev) => ({
        ...prev,
        [preguntaActual.id]: prev[preguntaActual.id] || esCorrecta,
      }));

      // Ajuste de tiempo y alerta
      if (esCorrecta) {
        setTiempoRestante((prevTime) => (prevTime ?? 0) + 10);
        setAlertaInfo({ esCorrecta: true, mensaje: "+10 Seg" });
      } else {
        setFallos((f) => f + 1);
        const nuevoTiempo = Math.max(0, (tiempoRestante ?? 0) - 5);
        setTiempoRestante(nuevoTiempo);
        if (nuevoTiempo === 0) {
          setTiempoAgotadoFlag(true);
        } else {
          setAlertaInfo({ esCorrecta: false, mensaje: "-5 Seg" });
        }
      }

      setMostrarAlerta(true);

      if (!esCorrecta) return;
    } catch (error) {
      console.error("Error al verificar respuesta desafío final:", error);
    }
  };

  // Helper para POST idempotente
  const postDF = async (correctas, incorrectas) => {
    const token = localStorage.getItem("token");
    await axios.post(
      DF_URL,
      {
        nivel_id: Number(nivelId),
        respuestas_correctas: Number(correctas),
        respuestas_incorrectas: Number(incorrectas),
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const avanzarPregunta = async () => {
    // Guarda progreso parcial
    const correctasParcial =
      Object.values(respuestasMap).filter(Boolean).length;
    await postDF(correctasParcial, fallos);

    const siguienteIndex = preguntaIndex + 1;
    if (siguienteIndex < preguntas.length) {
      setPreguntaIndex(siguienteIndex);

      const nextQ = preguntas[siguienteIndex];
      const respuestasValidas = (nextQ.respuestas || []).filter(
        (r) => (r.texto_respuesta || "").trim() !== ""
      );
      setPreguntaActual({
        ...nextQ,
        respuestas: shuffleArray(respuestasValidas),
      });

      setRespuestaSeleccionada(null);
      // El temporizador se reanudará automáticamente cuando cierres el overlay
      // (mostrarAlerta debe pasar a false desde el componente).
    } else {
      await finalizarNivel();
    }
  };

  const tiempoAgotado = () => {
    setMostrarAlerta(true);
    setAlertaInfo({ esCorrecta: false, mensaje: "Tiempo Agotado" });
  };

  const finalizarNivel = async () => {
    if (finalSaveOnce.current) return;
    finalSaveOnce.current = true;

    setNivelCompletado(true);

    const correctas = Object.values(respuestasMap).filter(Boolean).length;
    const incorrectas = fallos;

    try {
      await postDF(correctas, incorrectas); // guarda final
    } catch (error) {
      console.error("Error al guardar resultados desafío final:", error);
    }

    setMensaje({
      respuestasCorrectas: correctas,
      respuestasIncorrectas: incorrectas,
    });
    setMostrarResultado(true);
  };

  return {
    preguntaActual,
    tiempoRestante,
    nivelCompletado,
    tiempoAgotadoFlag,
    mostrarAlerta,
    alertaInfo,
    retroalimentacion,
    mensaje,
    mostrarResultado,
    formatTiempo,
    handleRespuesta,
    avanzarPregunta,
    setMostrarAlerta,
    finalizarNivel,
    setMostrarResultado,
  };
};

export default usePreguntasRespuestasFinal;
